import React, { useEffect, useState } from "react";
import { DatePicker, Form, Input, Card, Table, Button } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import { HomeOutlined, BranchesOutlined } from "@ant-design/icons";
import { getReportByBranches } from "../../../network/network";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { CSVLink } from "react-csv";
import CurrencyFormatter from "../../../components/CurrencyFormatter";
import { handleApiError } from "../../../utils/errorHandler";

const ReportByBranches = () => {
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [searchParams, setSearchParams] = useState([]);
  const [branchTransactionsData, setBranchTransactionsData] = useState([]);
  const [branchTransactionTypesData, setBranchTransactionTypesData] = useState(
    [],
  );
  const { t } = useTranslation();
  const [searchFormRef] = Form.useForm();
  const { RangePicker } = DatePicker;

  useEffect(() => {
    // Set Loading True
    setIsDataLoading(true);
    getReportByBranches(searchParams)
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setBranchTransactionsData(response.data.data.transactions_by_branch);
        setBranchTransactionTypesData(
          response.data.data.type_transactions_by_branch,
        );
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [searchParams, t]);

  const breadcrumbRoutes = [
    {
      path: "/",
      breadcrumbName: t("home"),
      icon: <HomeOutlined />,
    },
    {
      path: null,
      breadcrumbName: t("reports") + " / " + t("report_by_branch"),
      icon: <BranchesOutlined />,
    },
  ];

  const handleSearch = (values) => {
    let search_params = false;
    if (values.dates) {
      let startDateUtc = moment.utc(values.dates[0]).set({
        date: values.dates[0].date() - 1,
        month: values.dates[0].month(),
        year: values.dates[0].year(),
        hour: 19,
        minute: 0,
        second: 0,
        millisecond: 0,
      });
      let endDateUtc = moment.utc(values.dates[1]).set({
        date: values.dates[1].date(),
        month: values.dates[1].month(),
        year: values.dates[1].year(),
        hour: 18,
        minute: 59,
        second: 59,
        millisecond: 0,
      });

      search_params = {
        search_start_date: startDateUtc.format(),
        search_end_date: endDateUtc.format(),
      };
    }
    if (search_params) {
      setSearchParams(search_params);
    } else {
      setSearchParams([]);
    }
  };
  const handleReset = () => {
    // Reset Form Fields
    searchFormRef.resetFields();
    // Reset Transactions List
    setSearchParams([]);
  };

  const searchForm = (
    <>
      <Form
        name="search-report-form"
        layout="inline"
        onFinish={handleSearch}
        form={searchFormRef}
      >
        <Form.Item name="dates" label={t("date_range")}>
          <RangePicker allowClear format="DD/MM/YYYY" />
        </Form.Item>
        <Form.Item>
          <Input
            type="submit"
            value={t("search")}
            className="app-search-submit"
          />
        </Form.Item>
        <Form.Item>
          <Input type="button" value={t("reset")} onClick={handleReset} />
        </Form.Item>
      </Form>
    </>
  );

  const salesColumns = [
    {
      title: t("branches"),
      dataIndex: "name",
    },
    {
      title: t("gross_sales"),
      render: (data) => {
        return <>{<CurrencyFormatter value={data.total_charged_amount} />}</>;
      },
    },
    {
      title: t("refunds"),
      render: (data) => {
        return <>{<CurrencyFormatter value={data.total_refunded_amount} />}</>;
      },
    },
    {
      title: t("total_taxes_on_sale"),
      render: (data) => {
        return <>{<CurrencyFormatter value={data.total_tax} />}</>;
      },
    },
    {
      title: t("net_sales"),
      render: (data) => {
        return (
          <div className="app-font-bold">
            {
              <CurrencyFormatter
                value={
                  data.total_charged_amount -
                  Math.abs(data.total_refunded_amount) -
                  data.total_tax
                }
              />
            }
          </div>
        );
      },
    },
  ];

  const paymentsColumns = [
    {
      title: t("branches"),
      dataIndex: "name",
    },
    {
      title: t("cash"),
      render: (data) => {
        return <>{<CurrencyFormatter value={data.total_charged_amount_1} />}</>;
      },
    },
    {
      title: t("credit_card"),
      render: (data) => {
        return <>{<CurrencyFormatter value={data.total_charged_amount_4} />}</>;
      },
    },
    {
      title: t("multi_payment"),
      render: (data) => {
        return (
          <>{<CurrencyFormatter value={data.total_charged_amount_5} />} </>
        );
      },
    },
    {
      title: t("taxes_on_sale"),
      render: (data) => {
        return (
          <>
            {
              <CurrencyFormatter
                value={
                  Math.abs(data.total_tax_1) +
                  Math.abs(data.total_tax_4) +
                  Math.abs(data.total_tax_5)
                }
              />
            }
          </>
        );
      },
    },
    {
      title: t("net_sales"),
      render: (data) => {
        return (
          <div className="app-font-bold">
            {
              <CurrencyFormatter
                value={
                  Math.abs(data.total_charged_amount_1) +
                  Math.abs(data.total_charged_amount_4) +
                  Math.abs(data.total_charged_amount_5) -
                  (Math.abs(data.total_tax_1) +
                    Math.abs(data.total_tax_4) +
                    Math.abs(data.total_tax_5))
                }
              />
            }
          </div>
        );
      },
    },
  ];

  const salesCSVData = [
    [
      t("branches"),
      t("gross_sales"),
      t("refunds"),
      t("total_taxes_on_sale"),
      t("net_sales"),
    ],
    ...branchTransactionsData.map(
      ({ name, total_charged_amount, total_refunded_amount, total_tax }) => [
        name,
        Number(total_charged_amount).toFixed(2),
        Number(total_refunded_amount).toFixed(2),
        Number(total_tax).toFixed(2),
        Number(
          total_charged_amount - Math.abs(total_refunded_amount) - total_tax,
        ).toFixed(2),
      ],
    ),
  ];

  const paymentsCSVData = [
    [
      t("branches"),
      t("cash"),
      t("credit_card"),
      t("multi_payment"),
      t("taxes_on_sale"),
      t("net_sales"),
    ],
    ...branchTransactionTypesData.map(
      ({
        name,
        total_charged_amount_1,
        total_charged_amount_4,
        total_charged_amount_5,
        total_tax_1,
        total_tax_4,
        total_tax_5,
      }) => [
        name,
        Number(total_charged_amount_1).toFixed(2),
        Number(total_charged_amount_4).toFixed(2),
        Number(total_charged_amount_5).toFixed(2),
        Number(
          Math.abs(total_tax_1) + Math.abs(total_tax_4) + Math.abs(total_tax_5),
        ).toFixed(2),
        Number(
          Math.abs(total_charged_amount_1) +
            Math.abs(total_charged_amount_4) +
            Math.abs(total_charged_amount_5) -
            (Math.abs(total_tax_1) +
              Math.abs(total_tax_4) +
              Math.abs(total_tax_5)),
        ).toFixed(2),
      ],
    ),
  ];

  return (
    <>
      <Card className="app-mb-20">
        <CustomBreadcrumb menus={breadcrumbRoutes} />
        <PageHeader
          className="app-p-0"
          title={t("report_by_branch")}
          breadcrumb={CustomBreadcrumb}
          ghost={false}
          footer={searchForm}
          extra={[
            <Button type="primary" key="download-sales-report">
              <CSVLink
                data={salesCSVData}
                filename={
                  t("report_by_branch") +
                  " " +
                  moment().format("MM-DD-YYYY HH-mm") +
                  ".csv"
                }
                target="_blank"
              >
                {t("download_sales_report")}
              </CSVLink>
            </Button>,
            <Button type="primary" key="download-payment-report">
              <CSVLink
                data={paymentsCSVData}
                filename={
                  `${t("payments")} ${t("report_by_branch")}` +
                  " " +
                  moment().format("MM-DD-YYYY HH-mm") +
                  ".csv"
                }
                target="_blank"
              >
                {t("download_payments_report")}
              </CSVLink>
            </Button>,
          ]}
        />
      </Card>
      <Card className="app-mb-20">
        <h3>{t("sales")}</h3>
        <Table
          columns={salesColumns}
          dataSource={branchTransactionsData}
          loading={isDataLoading}
          rowKey="name"
          pagination={false}
        />
      </Card>
      <Card>
        <h3>{t("payments")}</h3>
        <Table
          columns={paymentsColumns}
          dataSource={branchTransactionTypesData}
          loading={isDataLoading}
          rowKey="name"
          pagination={false}
        />
      </Card>
    </>
  );
};

export default ReportByBranches;
